import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Input,
  DatePicker,
} from 'antd';

import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import ModalDelete from 'components/ModalDelete';
import getFormatedValues, { DATETIME_FRONTEND_FORMAT } from 'utils/formatValues';
import Select from 'components/Select';

import { getTiposDeCargos } from 'api/recaudacion/tiposDeCargo';
import NumericInput from 'components/NumericInput';
import { getPeriodosFiscales } from 'api/catalogos/periodosFiscales';
import { getClaseDeConceptosDeInfracciones } from 'api/catalogos/claseDeConceptoDeInfracciones';
import { getYardas } from 'api/catalogos/yardas';
import { getGruas } from 'api/catalogos/gruas';
import { getExamenesMedicos } from 'api/catalogos/examenMedico';
import {
  deleteConceptoDeInfraccion,
  getConceptosDeInfraccion,
  getConceptosDeInfracciones,
  patchConceptoDeInfracciones,
  postConceptosDeInfraccion,
} from 'api/recaudacion/conceptosDeInfracciones';
import { getTipoDeConceptoDeInfracciones } from 'api/recaudacion/tipoDeConceptosDeInfraccion';

export const configPermmission = {
  title: 'Concepto de Infracción',
  model: 'conceptodeinfraccion',
  catalogs: ['tipodecargo', 'periodofiscal', 'yarda', 'grua'],
};

const ConceptoDeInfracciones = ({ infraccion, permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [tipoDeCargoTemp, setTipoDeCargoTemp] = useState({ results: [] });
  const [periodoFiscal, setPeriodoFiscal] = useState([]);
  const [yarda, setYarda] = useState([]);
  const [gruas, setGruas] = useState([]);
  const [examenMedico, setExamenMedico] = useState([]);
  const [tipoDeConceptoDeInfraccion, setTipoDeConceptoDeInfraccion] = useState([]);

  const tipoDeCargo = useRef({ results: [] });

  const fetchData = async () => {
    setLoading(true);
    if (infraccion) {
      const _data = await getConceptosDeInfracciones({ infraccion });
      setData(_data);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _data,
        _periodosFiscales,
        _yarda,
        _gruas,
        _examen,
        _tipoDeConcepto,
      ] = await Promise.all([
        getConceptosDeInfracciones(),
        getPeriodosFiscales(),
        getClaseDeConceptosDeInfracciones(),
        getYardas(),
        getGruas(),
        getExamenesMedicos(),
        getTipoDeConceptoDeInfracciones(),

      ]);
      if (mounted) {
        setData(_data);
        setPeriodoFiscal(_periodosFiscales);
        setYarda(_yarda);
        setGruas(_gruas);
        setExamenMedico(_examen);
        setTipoDeConceptoDeInfraccion(_tipoDeConcepto);

        setLoading(false);
      }
    };
    if (permission.hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);
  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setVisibleAlert(false);
    setTipoDeCargoTemp({ results: [] });
    form.resetFields();
    tipoDeCargo.current = { results: [] };
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = getFormatedValues(form.getFieldsValue());
      const [key] = selectedRowKeys;
      values.infracion = infraccion;
      const saved = await (key ? patchConceptoDeInfracciones(key, values)
        : postConceptosDeInfraccion(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;

      const deleted = await deleteConceptoDeInfraccion({
        infraccionId: infraccion,
        conceptos: key,
      });
      if (deleted) {
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const onClickAdd = () => {
    setVisible(true);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onClickEdit = async (id) => {
    const key = selectedRowKeys[0] || id || id?.id;
    const match = await getConceptosDeInfraccion(key);
    if (match) {
      if (match?.tipo_de_cargo) {
        const _tipoDeCargo = await getTiposDeCargos({ id: match?.tipo_de_cargo });
        setTipoDeCargoTemp(_tipoDeCargo);
      } else {
        setTipoDeCargoTemp({ results: [] });
      }
    }
    setVisible(true);
    setLoading(false);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave_de_tipo_de_concepto',
      key: 'clave_de_tipo_de_concepto',
      width: 300,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion_de_tipo_de_concepto',
      key: 'descripcion_de_tipo_de_concepto',
      width: 200,
    },
  ];

  const rules = {
    required: [
      {
        required: true,
      },
    ],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            mobileColIndex={0}
            rowKey="id"
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            permission={permission}
          />
        ) : (
          <Card
            bordered={false}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
              />
            )}
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Concepto de Infraccion `}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clave_de_tipo_de_concepto"
                    label="Clave"
                    hasFeedback
                    rules={rules?.required}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="descripcion_de_tipo_de_concepto"
                    label="Descripción"
                    hasFeedback
                    rules={rules?.required}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="concepto_activo"
                    label="¿Es Concepto Activo?"
                    hasFeedback
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="Importe en Moneda"
                    name="importe_en_moneda"
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="Importe"
                    name="importe"
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="nota_de_concepto"
                    label="Nota de Concepto"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="fecha_inicio"
                    label="Fecha de Inicio"
                  >
                    <DatePicker
                      placeholder=""
                      format={DATETIME_FRONTEND_FORMAT}
                      showTime
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="fecha_final"
                    label="Fecha Final"
                  >
                    <DatePicker
                      placeholder=""
                      format={DATETIME_FRONTEND_FORMAT}
                      showTime
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_cargo"
                    label="Tipo de Cargo"
                    hasFeedback
                    rules={rules?.required}
                  >
                    <Select
                      dataSource={tipoDeCargoTemp.results?.length
                        ? tipoDeCargoTemp : tipoDeCargo.current}
                      usingExternalDataSource={Boolean(tipoDeCargoTemp.results?.length)}
                      labelProp="descripcion"
                      onClear={() => { form.resetFields(['tipo_de_cargo']); setTipoDeCargoTemp({ results: [] }); }}
                      pagination={{
                        getter: getTiposDeCargos,
                        setter: (dataSource) => {
                          tipoDeCargo.current = dataSource;
                        },
                        config: { auto: true, qName: 'descripcion' },
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="Importe de Moneda Fija Inicial"
                    name="importe_de_moneda_fija_inicial"
                    required
                    decimal
                    maxLength={32}
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="Importe de Moneda Minima"
                    name="importe_de_moneda_minima"
                    required
                    decimal
                    maxLength={32}
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="Importe de Moneda Máxima"
                    name="importe_de_moneda_maxima"
                    required
                    decimal
                    maxLength={32}
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="periodo_fiscal"
                    label="Periodo Fiscal"
                    hasFeedback
                  >
                    <Select
                      dataSource={periodoFiscal}
                      render={(e) => `${e.periodo} - ${e.identificador}${e.vigente ? ' (Vigente)' : ''}`}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_concepto_de_infraccion"
                    label="Tipo de Concepto de Infracción"
                    hasFeedback
                  >
                    <Select
                      dataSource={tipoDeConceptoDeInfraccion}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="grua"
                    label="Grua"
                    hasFeedback
                  >
                    <Select dataSource={gruas} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="examen_medico"
                    label="Exámen Médico"
                    hasFeedback
                  >
                    <Select dataSource={examenMedico} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="yarda"
                    label="Yarda"
                    hasFeedback
                  >
                    <Select dataSource={yarda} />
                  </Form.Item>
                </Col>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Concepto ${form.getFieldValue('descripcion_de_tipo_de_concepto')}`}
        />
      </Spin>
    </Row>
  );
};
ConceptoDeInfracciones.propTypes = {
  infraccion: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
    hasAllPermission: PropTypes.bool.isRequired,
  }).isRequired,
};

ConceptoDeInfracciones.defaultProps = {
  infraccion: null,
};
export default ConceptoDeInfracciones;
